import { FC, MouseEvent, useMemo, useState } from 'react';

import { DeleteItemButton, EditItemButton, Item, ItemsContainer, ItemsCount } from './Inspirations-styles';
import {
	Department,
	InspirationItem,
	InspirationItemId,
	useDeleteInspirationItemMutation,
	useGetInspirationsInfiniteQuery
} from './redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import PageLayout from '@/components/page-layout.tsx';
import TableActions from '@/components/table/table-actions.tsx';
import { menuFilters, quickFilters } from './filters.ts';
import { useURLState } from '@/hooks';
import GenericActionModal from '@/components/generic-action-modal.tsx';
import { InspirationItemDetailsModal } from './components';

export const Inspirations: FC = () => {
	const {
		urlState: { department, search }
	} = useURLState<{ department: Department; search: string }>();
	const [selectedItem, setSelectedItem] = useState<InspirationItem | void>();
	const [showItemModal, setShowItemModal] = useState<{ action: 'add' | 'edit'; id?: InspirationItemId } | false>(
		false
	);
	const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);

	const {
		data: inspirationsData,
		fetchNextPage,
		hasNextPage
	} = useGetInspirationsInfiniteQuery({ department, search });

	const [deleteItem] = useDeleteInspirationItemMutation();

	const inspirationItems = useMemo(
		() => inspirationsData?.pages.flatMap((page) => page.items) || [],
		[inspirationsData]
	);

	const actionsList = [
		{
			text: 'Add new',
			onActivate: () => setShowItemModal({ action: 'add' })
		}
	];

	const onEditClick = (event: MouseEvent, itemId: InspirationItemId) => {
		event.stopPropagation();
		setShowItemModal({ id: itemId, action: 'edit' });
	};

	return (
		<PageLayout>
			{showItemModal && (
				<InspirationItemDetailsModal
					action={showItemModal.action}
					itemId={showItemModal.id}
					onClose={() => setShowItemModal(false)}
				/>
			)}
			{showDeleteItemModal && (
				<GenericActionModal
					title="Delete item"
					body="This will permanently delete this inspiration item from the system"
					actionButtonText="Delete"
					onClose={() => setShowDeleteItemModal(false)}
					onSubmit={async () => {
						if (selectedItem) {
							await deleteItem(selectedItem.id);
							setShowDeleteItemModal(false);
							setSelectedItem();
						}
					}}
				/>
			)}
			<TableActions
				tableTitle="Inspirations"
				selectedRows={selectedItem ? [selectedItem.id] : []}
				onDeselectAll={() => setSelectedItem()}
				onDelete={() => setShowDeleteItemModal(true)}
				quickFilters={quickFilters}
				filters={menuFilters}
				actionsList={actionsList}
			/>
			<ItemsCount>{inspirationsData?.pages[0]?.pageInfo.totalItems} photos</ItemsCount>
			<InfiniteScroll
				dataLength={inspirationItems.length}
				next={fetchNextPage}
				hasMore={hasNextPage}
				loader={null}>
				<ItemsContainer>
					{inspirationItems.map((item) => (
						<Item
							key={item.id}
							onClick={() => setSelectedItem(item)}
							$isSelected={selectedItem?.id === item.id}>
							<img src={item.imageUrl} alt="" />
							<div>
								<EditItemButton aria-label="edit" onClick={(event) => onEditClick(event, item.id)} />
								<DeleteItemButton aria-label="delete" onClick={() => setShowDeleteItemModal(true)} />
							</div>
						</Item>
					))}
				</ItemsContainer>
			</InfiniteScroll>
		</PageLayout>
	);
};
